export interface Candle {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

// Symbol is managed externally by the host app via this module-level variable
let _currentSymbol = 'EURUSD';

export function setSymbol(symbol: string) {
  if (symbol === _currentSymbol) return;
  _currentSymbol = symbol;
  // Reload per-symbol state so drawings/indicators survive an asset switch.
  // Imported lazily to avoid a circular dependency (stores import from dataUtils).
  if (typeof window !== 'undefined') {
    Promise.all([
      import('../store/drawingStore'),
      import('../store/indicatorStore'),
    ]).then(([d, i]) => {
      d.reloadDrawingsFromStorage();
      i.reloadIndicatorsFromStorage();
    }).catch(() => { /* ignore */ });
  }
}

export function getSymbol(): string {
  return _currentSymbol;
}

export function resample(candles: Candle[], minutes: number): Candle[] {
  const groupSize = Math.max(1, Math.round(minutes / 5));
  if (groupSize <= 1) return candles;

  const result: Candle[] = [];
  for (let i = 0; i < candles.length; i += groupSize) {
    const group = candles.slice(i, i + groupSize);
    if (group.length === 0) continue;
    result.push({
      time: group[0].time,
      open: group[0].open,
      high: Math.max(...group.map((c) => c.high)),
      low: Math.min(...group.map((c) => c.low)),
      close: group[group.length - 1].close,
      volume: group.reduce((sum, c) => sum + c.volume, 0),
    });
  }
  return result;
}

export function tfLabel(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes === 60) return '1h';
  if (minutes === 240) return '4h';
  if (minutes === 1440) return '1D';
  if (minutes === 10080) return '1W';
  return `${minutes}m`;
}

export function chartTypeLabel(type: string): string {
  switch (type) {
    case 'candle': return 'Candles';
    case 'bar': return 'Bars';
    case 'line': return 'Line';
    case 'area': return 'Area';
    case 'hlc': return 'HLC';
    case 'dots': return 'Dots';
    case 'heikin': return 'Heikin-Ashi';
    default: return 'Candles';
  }
}

// Heikin-Ashi smoothing. HA-close = avg(o,h,l,c); HA-open = (prev HA-open + prev HA-close)/2;
// HA-high = max(h, HA-open, HA-close); HA-low = min(l, HA-open, HA-close).
export function toHeikinAshi(candles: Candle[]): Candle[] {
  if (candles.length === 0) return [];
  const out: Candle[] = [];
  let prevOpen = candles[0].open;
  let prevClose = candles[0].close;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const haOpen = i === 0 ? (c.open + c.close) / 2 : (prevOpen + prevClose) / 2;
    const haHigh = Math.max(c.high, haOpen, haClose);
    const haLow = Math.min(c.low, haOpen, haClose);
    out.push({ time: c.time, open: haOpen, high: haHigh, low: haLow, close: haClose, volume: c.volume });
    prevOpen = haOpen;
    prevClose = haClose;
  }
  return out;
}
